/**
 * usage-log — append-only per-call usage events with cost.
 *
 * Source: Issue #2469 (epic #2467 child).
 *
 * For operators running against metered API gateways, per-call cost +
 * tokens + latency is the data they need to manage spend. This module
 * provides three things:
 *
 *   1. `recordUsageEvent(event)` — append a per-call record to a JSONL
 *      log under <NEXUS_DATA_DIR>/usage/usage-YYYY-MM.jsonl.
 *   2. `loadUsageEvents({...})` — read events for a window, filtered
 *      by model / category.
 *   3. `computeCostDetail(modelId, inputTokens, outputTokens)` (and its
 *      thin wrapper `computeCostUSD`) — compute cost from the FULL model
 *      registry pricing chain (manifest > in-tree > models.dev > generated
 *      LiteLLM catalog), including the #4164 normalized/identity resolution
 *      tier for decorated gateway model ids. A model with no pricing
 *      anywhere in the chain is reported as `priced: false` so consumers
 *      can treat the missing cost as UNMEASURED (#3855), never a real $0.
 *
 * The `usage` CLI command (cli/usage-command.ts) consumes this for the
 * operator dashboard. Existing OutcomeStore is intentionally untouched
 * — its schema is for routing/learning signals, not billing.
 */

import { z } from 'zod';
import { appendFileSync, existsSync, mkdirSync, readFileSync, readdirSync } from 'node:fs';
import { computeTokenCost, roundToMicroUsd } from './token-cost-core.js';
import { dirname, join } from 'node:path';

import { getNexusDataDir } from '../config/nexus-data-dir.js';
import { createLogger } from '../core/logger.js';
// NOTE: only the FUNCTION is imported here — `getDefaultRegistry()` must never
// be CALLED at module scope (#3185 bootstrap hazard: first construction reads
// the manifest overlay/snapshot from disk). All calls happen at invocation
// time inside computeCostDetail.
import { getDefaultRegistry, type MatchedVia } from '../config/model-registry.js';
import type { PriceBasis } from '../core/price-basis.js';

export interface UsageEvent {
  /** ISO 8601 timestamp of the call. */
  readonly timestamp: string;
  /** Model identifier (e.g., 'claude-sonnet-4', 'gpt-4o'). */
  readonly modelId: string;
  /** Provider/adapter (e.g., 'anthropic', 'openai', 'openai-compat'). */
  readonly providerId: string;
  /** Token counts. */
  readonly inputTokens: number;
  readonly outputTokens: number;
  /**
   * Cost in USD. Computed at write time from the full model-registry pricing
   * chain (see `computeCostDetail`). 0 with `priced: false` means the model
   * was UNPRICED (cost unknown), not a real $0 — check `priced` before
   * treating this as a measured figure.
   */
  readonly usdCost: number;
  /** Wall-clock latency in milliseconds. */
  readonly latencyMs: number;
  /** Whether the call succeeded. */
  readonly success: boolean;
  /**
   * Optional task category — populated when the call was made on behalf of
   * a routed task (so aggregation can roll up by category).
   */
  readonly category?: string;
  /** Optional failure code when success === false. */
  readonly errorCode?: string;
  /**
   * Whether pricing data existed for the model at write time (#4165), so
   * audit can distinguish a real $0 from an unpriced model. Absent on lines
   * written before this field existed.
   */
  readonly priced?: boolean;
  /**
   * Pricing provenance: the canonical registry id whose pricing was applied
   * (the entry's `resolvedFrom` when the #4164 fuzzy tier matched a decorated
   * id, else the caller's model id) — or, for a call a gateway served under a
   * `free`/`local`/`priced:<in>,<out>` declaration, the gateway's
   * `api:<endpoint>` arm, since the declaration and not a registry entry
   * supplied the number (#4392 step 4). Present only when `priced` is true.
   */
  readonly priceSource?: string;
}

/** Result of a pricing-aware cost computation (#4165). */
export interface CostDetail {
  /** Cost in USD. Always 0 when `priced` is false — an unknown, not a $0. */
  readonly costUsd: number;
  /** Whether the resolved registry entry carried pricing data. */
  readonly priced: boolean;
  /**
   * Canonical id the pricing/metadata came from: the entry's `resolvedFrom`
   * when the #4164 fuzzy tier matched a decorated id, else the caller's id.
   */
  readonly resolvedId: string;
  /** Fuzzy-resolution provenance (#4164), passed through from the entry. */
  readonly matchedVia?: MatchedVia;
}

/**
 * Compute cost in USD given a model and token counts, with pricing
 * provenance. Reads the FULL registry chain via
 * `getDefaultRegistry().getEntry()` — manifest > in-tree > models.dev >
 * generated LiteLLM catalog, plus the #4164 normalized/identity tier that
 * resolves decorated model names from OpenAI-compatible gateways to their
 * canonical entry's pricing.
 *
 * `priced: false` (with `costUsd: 0`) means NO pricing existed anywhere in
 * the chain — consumers must treat the cost as UNMEASURED (#3855), never a
 * real $0. Operators can add pricing for gateway-only models via the
 * models-manifest overlay.
 */
export function computeCostDetail(
  modelId: string,
  inputTokens: number,
  outputTokens: number
): CostDetail {
  // Registry read happens at INVOCATION time, never module load — first
  // construction touches the filesystem (#3185 bootstrap hazard).
  const entry = getDefaultRegistry().getEntry(modelId);
  const resolvedId = entry.resolvedFrom ?? modelId;
  const provenance = entry.matchedVia !== undefined ? { matchedVia: entry.matchedVia } : {};
  if (entry.pricing === undefined) {
    return { costUsd: 0, priced: false, resolvedId, ...provenance };
  }
  // The arithmetic lives in the shared core (#5122); this function is the
  // REGISTRY-BACKED, LEDGER-ROUNDED wrapper over it. Rounding to micro-USD
  // stays here rather than in the core: it exists so this log's JSONL does not
  // drift into floating-point noise across many small calls, which is a ledger
  // requirement, not a property of cost.
  const { costUsd } = computeTokenCost(
    { input: inputTokens, output: outputTokens },
    { inputPer1M: entry.pricing.inputPer1M, outputPer1M: entry.pricing.outputPer1M }
  );
  return { costUsd: roundToMicroUsd(costUsd), priced: true, resolvedId, ...provenance };
}

/**
 * The {@link PriceBasis} the cost in a {@link CostDetail} rests on (#4406).
 *
 * DELIBERATELY derived rather than stored, because `CostDetail.priced` already
 * carries the only distinction the current two-member union can express: the
 * chain resolved a rate, or it did not. A `priceBasis` property beside `priced`
 * would be a second spelling of the same boolean.
 *
 * WHAT THE `'list'` VALUE ASSERTS. Only that a rate was resolved, and that it
 * should be read as an assumed published rate. It is NOT a guarantee that the
 * number is a vendor's advertised public rate — at least three paths put
 * something else behind the label, and one puts a real published rate behind
 * `'unknown'`:
 *
 *  - The operator manifest overlay is the highest-precedence tier and passes
 *    `pricing` through; overriding pricing is its purpose. A negotiated rate
 *    entered there reports `'list'`, and the caveat then warns the reader their
 *    contract may differ over the contract rate.
 *  - The normalized/fuzzy identity tier lets a decorated gateway id inherit a
 *    DIFFERENT canonical entry's rate.
 *  - A gateway's `NEXUS_GATEWAY_COST` declaration (`free`, `local`,
 *    `priced:<in>,<out>`) is the operator's own statement, not a published
 *    rate at all; `gatewayCostDetail` (#4392 step 4) reports it `priced: true`
 *    with the arm as `resolvedId`, so it reads `'list'` here too.
 *  - `config/models-generated-loader.ts` discards a published $0/$0 rate unless
 *    the id ends `:free`, so `'unknown'` does not mean no price exists.
 *
 * The derivation is therefore an assumption about the chain, not a fact about
 * it — conservative in the right direction (it invites a caveat rather than
 * suppressing one), and honest only if described this way. Narrowing it needs a
 * per-entry label the registry does not carry today; see {@link PriceBasis}.
 *
 * `priceSource`, the other provenance field, answers a different question: WHICH
 * registry entry supplied the number, not what KIND of rate it is. It does not
 * encode the basis, which is why the basis has to travel separately into records
 * (the decision-cost store) that persist neither field.
 */
export function priceBasisOf(detail: CostDetail): PriceBasis {
  return detail.priced ? 'list' : 'unknown';
}

/**
 * Thin wrapper over {@link computeCostDetail} returning only the USD figure.
 * Returns 0 both for a genuinely free model and for a model with no pricing
 * data — callers that must tell those apart (unpriced ⇒ UNMEASURED, #3855)
 * should use `computeCostDetail` and check `priced`.
 */
export function computeCostUSD(modelId: string, inputTokens: number, outputTokens: number): number {
  return computeCostDetail(modelId, inputTokens, outputTokens).costUsd;
}

/** Resolve the active usage log path for the current month. */
export function getUsageLogPath(date: Date = new Date()): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return join(getNexusDataDir(), 'usage', `usage-${String(year)}-${month}.jsonl`);
}

/**
 * Append a usage event to the current month's log. Best-effort — failures
 * are silent (we don't want to fail a successful model call because we
 * couldn't write a log line).
 */
export function recordUsageEvent(event: UsageEvent): void {
  try {
    const path = getUsageLogPath(new Date(event.timestamp));
    mkdirSync(dirname(path), { recursive: true });
    appendFileSync(path, `${JSON.stringify(event)}\n`, 'utf-8');
  } catch {
    // Intentionally silent — telemetry must not break user calls.
  }
}

export interface LoadUsageOptions {
  /** Restrict to events at or after this ISO timestamp. */
  readonly sinceIso?: string;
  /** Restrict to events before this ISO timestamp. */
  readonly untilIso?: string;
  /** Only events for this model. */
  readonly modelId?: string;
  /** Only events for this category. */
  readonly category?: string;
}

interface UsageFileList {
  readonly files: readonly string[];
  readonly readErrors: readonly string[];
}

function describeReadError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function listUsageFiles(dir: string): UsageFileList {
  if (!existsSync(dir)) return { files: [], readErrors: [] };
  try {
    const files = readdirSync(dir).filter(
      (file) => file.startsWith('usage-') && file.endsWith('.jsonl')
    );
    return { files, readErrors: [] };
  } catch (error) {
    return {
      files: [],
      readErrors: [`usage ledger unreadable: failed to list ${dir}: ${describeReadError(error)}`],
    };
  }
}

interface LoadFilter {
  readonly sinceMs: number;
  readonly untilMs: number;
  readonly modelId: string | undefined;
  readonly category: string | undefined;
}

const logger = createLogger({ component: 'usage-log' });

/**
 * Validate a ledger line before it is trusted (#5328).
 *
 * The read path did `JSON.parse(line) as UsageEvent` — a cast. `eventMatches`
 * only inspects `timestamp` / `modelId` / `category`, so a corrupt `usdCost`
 * reached `rollupByModel`'s `reduce((s, e) => s + e.usdCost, 0)`: a string
 * concatenates, a missing value yields `NaN`, and neither throws. Every sibling
 * JSONL reader here validates; this one did not, and it is the cost ledger.
 *
 * Optional fields stay optional ON PURPOSE. `category`, `errorCode`, `priced`
 * and `priceSource` all postdate the original format — `priced` is documented
 * as "Absent on lines written before this field existed" — so requiring them
 * would silently discard real spend history, which is worse than the corruption
 * being fixed.
 *
 * Not `.strict()`, for the same forward-compatibility reason: a line carrying a
 * field a newer version writes must still load in an older one.
 *
 * `z.number()` already rejects `NaN` and infinities in zod v4, which is the
 * property that matters here: a cost that is not a real number cannot be summed,
 * and JSON renders `NaN` as `null`, which this also rejects.
 */
const UsageEventSchema = z.object({
  timestamp: z.string(),
  modelId: z.string(),
  providerId: z.string(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  usdCost: z.number(),
  latencyMs: z.number(),
  success: z.boolean(),
  category: z.string().optional(),
  errorCode: z.string().optional(),
  priced: z.boolean().optional(),
  priceSource: z.string().optional(),
});

/**
 * Narrow a validated line to {@link UsageEvent}.
 *
 * Zod's `.optional()` yields `T | undefined`, which `exactOptionalPropertyTypes`
 * will not assign to a `?: T` property — an absent key and a present-but-
 * undefined key are different things under that flag, and the distinction is
 * the right one for a ledger: a line that omits `priced` is legacy, whereas one
 * that sets it to `undefined` is malformed.
 */
function toUsageEvent(v: z.infer<typeof UsageEventSchema>): UsageEvent {
  return {
    timestamp: v.timestamp,
    modelId: v.modelId,
    providerId: v.providerId,
    inputTokens: v.inputTokens,
    outputTokens: v.outputTokens,
    usdCost: v.usdCost,
    latencyMs: v.latencyMs,
    success: v.success,
    ...(v.category !== undefined && { category: v.category }),
    ...(v.errorCode !== undefined && { errorCode: v.errorCode }),
    ...(v.priced !== undefined && { priced: v.priced }),
    ...(v.priceSource !== undefined && { priceSource: v.priceSource }),
  };
}

function eventMatches(parsed: UsageEvent, f: LoadFilter): boolean {
  const ts = Date.parse(parsed.timestamp);
  if (ts < f.sinceMs || ts >= f.untilMs) return false;
  if (f.modelId !== undefined && parsed.modelId !== f.modelId) return false;
  if (f.category !== undefined && parsed.category !== f.category) return false;
  return true;
}

interface ParsedUsageFile {
  readonly events: readonly UsageEvent[];
  readonly readError?: string;
}

function parseFileLines(filePath: string, filter: LoadFilter): ParsedUsageFile {
  let content: string;
  try {
    content = readFileSync(filePath, 'utf-8');
  } catch (error) {
    return { events: [], readError: `${filePath}: ${describeReadError(error)}` };
  }
  const out: UsageEvent[] = [];
  let rejected = 0;
  for (const line of content.split('\n')) {
    if (line.trim() === '') continue;
    let raw: unknown;
    try {
      raw = JSON.parse(line);
    } catch {
      // Skip malformed line; keep reading.
      rejected++;
      continue;
    }
    const parsed = UsageEventSchema.safeParse(raw);
    if (!parsed.success) {
      rejected++;
      continue;
    }
    const event = toUsageEvent(parsed.data);
    if (eventMatches(event, filter)) out.push(event);
  }
  // Say so rather than quietly returning a shorter list: a ledger that drops
  // lines silently under-reports spend, and the operator has no way to tell.
  if (rejected > 0) {
    logger.warn('Usage ledger lines rejected as unreadable', { filePath, rejected });
  }
  return { events: out };
}

interface UsageLedgerLoadResult {
  /** Valid events recovered from every readable ledger file. */
  readonly events: readonly UsageEvent[];
  /** True only when the ledger directory and every discovered file were readable. */
  readonly complete: boolean;
  /** Human-readable directory/file failures that made the result incomplete. */
  readonly readErrors: readonly string[];
}

/**
 * Load all usage events from disk that match the filter, preserving whether
 * every ledger file was readable. A missing usage directory is a complete,
 * empty ledger. For sub-second filtering at scale a future PR can index by
 * month, but linear scan is fine at the "operator dashboard" scale.
 */
export function loadUsageEvents(opts: LoadUsageOptions = {}): UsageLedgerLoadResult {
  const dir = join(getNexusDataDir(), 'usage');
  const listed = listUsageFiles(dir);
  if (listed.files.length === 0) {
    return { events: [], complete: listed.readErrors.length === 0, readErrors: listed.readErrors };
  }
  const filter: LoadFilter = {
    sinceMs: opts.sinceIso !== undefined ? Date.parse(opts.sinceIso) : Number.NEGATIVE_INFINITY,
    untilMs: opts.untilIso !== undefined ? Date.parse(opts.untilIso) : Number.POSITIVE_INFINITY,
    modelId: opts.modelId,
    category: opts.category,
  };
  const events: UsageEvent[] = [];
  const readErrors: string[] = [];
  for (const file of listed.files) {
    const parsed = parseFileLines(join(dir, file), filter);
    events.push(...parsed.events);
    if (parsed.readError !== undefined) readErrors.push(parsed.readError);
  }
  if (readErrors.length === listed.files.length) {
    readErrors[0] = `usage ledger unreadable: no files readable; ${readErrors[0] ?? ''}`;
  }
  return { events, complete: readErrors.length === 0, readErrors };
}

export interface ModelRollup {
  readonly modelId: string;
  readonly providerId: string;
  readonly callCount: number;
  readonly successCount: number;
  readonly successRate: number;
  readonly totalInputTokens: number;
  readonly totalOutputTokens: number;
  readonly totalUsdCost: number;
  /** Calls whose cost is unknown; legacy events without `priced` do not count. */
  readonly unpricedCallCount: number;
  readonly avgLatencyMs: number;
  /** Null when there were no successful calls. */
  readonly costPerSuccessUsd: number | null;
}

/**
 * Aggregate events into per-model rollups. Sorted by total cost descending
 * — the model burning the most money at top. Useful for "where is my spend
 * going?" investigations.
 */
export function rollupByModel(events: readonly UsageEvent[]): readonly ModelRollup[] {
  if (events.length === 0) return [];
  const groups = new Map<string, UsageEvent[]>();
  for (const e of events) {
    const arr = groups.get(e.modelId);
    if (arr === undefined) groups.set(e.modelId, [e]);
    else arr.push(e);
  }
  const rollups: ModelRollup[] = [];
  for (const [modelId, group] of groups) {
    const callCount = group.length;
    const successCount = group.filter((e) => e.success).length;
    const totalInputTokens = group.reduce((s, e) => s + e.inputTokens, 0);
    const totalOutputTokens = group.reduce((s, e) => s + e.outputTokens, 0);
    const totalUsdCost = group.reduce((s, e) => s + e.usdCost, 0);
    // Legacy ledger lines predate `priced`; only an explicit false is unpriced.
    const unpricedCallCount = group.filter((event) => event.priced === false).length;
    const totalLatency = group.reduce((s, e) => s + e.latencyMs, 0);
    const successRate = callCount === 0 ? 0 : successCount / callCount;
    const avgLatencyMs = callCount === 0 ? 0 : totalLatency / callCount;
    const costPerSuccessUsd = successCount === 0 ? null : totalUsdCost / successCount;
    rollups.push({
      modelId,
      providerId: group[0]?.providerId ?? 'unknown',
      callCount,
      successCount,
      successRate,
      totalInputTokens,
      totalOutputTokens,
      totalUsdCost,
      unpricedCallCount,
      avgLatencyMs,
      costPerSuccessUsd,
    });
  }
  return rollups.sort((a, b) => b.totalUsdCost - a.totalUsdCost);
}
